import React from "react";
import { Button } from "../ui/button";
import { ArrowRight, Camera } from "lucide-react";
import { MAIN_APP_URL } from "../../lib/config";

/**
 * Landing hero — new positioning ("Reuse. Remove. Clear.").
 *
 * Left column: the promise + two CTAs.
 * Right column: a single, minimal snap-a-photo mock that reinforces the
 * product's core interaction without leaning on stock photography.
 */
export default function Hero() {
  return (
    <section
      id="top"
      className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24 bg-gradient-to-b from-sage-50 via-cream-50 to-cream-50"
      data-testid="hero"
    >
      {/* Soft green depth layers */}
      <div
        aria-hidden
        className="absolute -top-24 -right-20 w-[520px] h-[520px] rounded-full bg-sage-200/40 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-moss-200/40 blur-3xl pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Copy */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-moss-900 text-cream-50 text-[11px] font-semibold tracking-[0.16em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-sage-300 animate-pulse" />
            Now live in North Wales and the North West
          </div>

          <h1
            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-extrabold leading-[0.98] tracking-tight text-moss-950 text-balance"
            data-testid="hero-headline"
          >
            Reuse.{" "}
            <span className="text-sage-600">Remove.</span>{" "}
            Clear.
          </h1>

          <p
            className="mt-6 text-lg sm:text-xl text-moss-800 leading-relaxed max-w-2xl"
            data-testid="hero-subheadline"
          >
            Binshare is a waste marketplace. Post a job free, compare fixed
            quotes from registered waste carriers, and pay after collection.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href={MAIN_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-post-job"
            >
              <Button className="h-14 px-8 rounded-full bg-moss-900 hover:bg-moss-950 text-cream-50 text-base font-semibold shadow-lg shadow-moss-900/20 w-full sm:w-auto">
                Post a job for free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a
              href={MAIN_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-join-carrier"
            >
              <Button
                variant="outline"
                className="h-14 px-8 rounded-full bg-transparent border-2 border-moss-900 text-moss-900 hover:bg-moss-900 hover:text-cream-50 text-base font-semibold w-full sm:w-auto"
              >
                Join as a carrier
              </Button>
            </a>
          </div>

          <p
            className="mt-4 text-sm font-medium text-moss-700"
            data-testid="hero-risk-reversal"
          >
            Post for free. Get quotes. Pay after pickup.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-moss-700">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage-500" />
              Registered waste carriers only
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage-500" />
              £500 Flytipping Guarantee, subject to terms
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage-500" />
              Duty-of-care receipt included
            </div>
          </div>
        </div>

        {/* Visual */}
        <div className="lg:col-span-5">
          <div className="relative mx-auto max-w-md">
            {/* Phone frame */}
            <div className="relative rounded-[2.4rem] bg-moss-950 p-3 shadow-2xl shadow-moss-900/30 border border-moss-800">
              <div className="rounded-[1.9rem] overflow-hidden bg-gradient-to-br from-moss-800 via-moss-900 to-moss-950 aspect-[9/17] flex flex-col">
                {/* Top bar */}
                <div className="flex items-center justify-between px-5 pt-6 pb-3 text-cream-50/70 text-xs">
                  <span>9:41</span>
                  <span className="flex gap-1">
                    <span className="w-4 h-2 rounded-sm bg-cream-50/40" />
                    <span className="w-4 h-2 rounded-sm bg-cream-50/70" />
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 px-6 pt-4 flex flex-col">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-sage-300 font-semibold">
                    New job
                  </p>
                  <p className="text-cream-50 text-2xl font-bold leading-tight mt-1">
                    Snap what needs clearing
                  </p>

                  <div className="mt-5 rounded-2xl bg-cream-50/5 border border-cream-50/10 backdrop-blur p-4 flex items-center justify-center aspect-square">
                    <div className="w-20 h-20 rounded-full bg-sage-500/20 flex items-center justify-center border border-sage-400/30">
                      <Camera className="w-9 h-9 text-sage-200" />
                    </div>
                  </div>

                  <p
                    className="mt-4 text-[10px] uppercase tracking-[0.16em] text-cream-50/60 font-semibold"
                    data-testid="hero-example-jobs-label"
                  >
                    Example jobs
                  </p>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center justify-between rounded-xl bg-cream-50/5 border border-cream-50/10 px-3 py-2.5">
                      <div className="text-cream-50/80 text-xs">
                        Post office clearance · Caernarfon
                      </div>
                      <div className="text-sage-300 text-[11px] font-semibold">
                        Example quote
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-cream-50/5 border border-cream-50/10 px-3 py-2.5">
                      <div className="text-cream-50/80 text-xs">
                        Garden waste · Bangor
                      </div>
                      <div className="text-sage-300 text-[11px] font-semibold">
                        Example quote
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-cream-50/5 border border-cream-50/10 px-3 py-2.5">
                      <div className="text-cream-50/80 text-xs">
                        Old fridge · Llandudno
                      </div>
                      <div className="text-sage-300 text-[11px] font-semibold">
                        Example quote
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating quote card */}
            <div className="hidden sm:flex absolute -left-6 bottom-14 rotate-[-4deg] items-center gap-3 rounded-2xl bg-cream-50 shadow-xl shadow-moss-900/10 border border-moss-100/60 px-4 py-3">
              <span className="w-8 h-8 rounded-full bg-sage-500/20 text-sage-700 flex items-center justify-center font-bold text-sm">
                £
              </span>
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.16em] text-moss-500 font-semibold">
                  Quote received
                </p>
                <p className="text-sm font-semibold text-moss-900">
                  Example quote
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
